import React, { useState } from 'react';

import { Separator } from '@change/ds-core-ui';
import { FacebookRegularIcon as FacebookIcon } from '@change/ds-icons';
import { cn } from '@change/ds-utils';

import { Circle, Rectangle, SkeletonContainer } from '../../../LoadingSkeletons';

import { RectangleShapeStyles, SkeletonContainerStyles } from './Header.styles';
import { GroupHeaderStyles, GroupImageStyles, GroupNameStyles } from './HeaderGroup.styles';
import { TextSkeleton1Styles, TextSkeleton2Styles, TextSkeletonContainerStyles } from './HeaderTextSkeletons.styles';
import type { FacebookGroupPostComponentsProps } from './types';

function DefaultFacebookIcon({ size }: { size: 'xsmall' | 'small' | 'medium' | 'large' }) {
	let iconSize: number;
	if (size === 'xsmall' || size === 'small') {
		iconSize = 10;
	} else if (size === 'medium') {
		iconSize = 20;
	} else {
		iconSize = 24;
	}
	return (
		<div className="flex h-full w-full items-center justify-center bg-[#D1E4FC]">
			<FacebookIcon className="text-[#0866FF]" size={iconSize} />
		</div>
	);
}

export function Header(props: FacebookGroupPostComponentsProps): React.JSX.Element {
	const {
		loading,
		size,
		retryState,
		recommendedGroup: { name: groupName = '', metadata: { imageUrl } = {} } = {},
	} = props;
	const [imageLoadError, setImageLoadError] = useState(false);

	// Disable animation when in failed retry state (static background for better contrast)
	const shouldAnimate = loading && retryState !== 'failed';

	if (size === 'xsmall' && loading) {
		return (
			<SkeletonContainer
				loading={shouldAnimate}
				shapeSize={size}
				className="h-full w-full"
				data-testid="skeleton-container"
			>
				<Rectangle className="h-full w-full" data-testid="rectangle" />
			</SkeletonContainer>
		);
	}

	const rectangleStyles = RectangleShapeStyles({ size });

	return (
		<div>
			{loading && (
				<SkeletonContainer loading={shouldAnimate} shapeSize={size} className={GroupHeaderStyles({ size })}>
					<Circle data-testid="circle" className={GroupImageStyles({ size })} />
					<div className={TextSkeletonContainerStyles({ size })}>
						<Rectangle className={cn(rectangleStyles, TextSkeleton1Styles({ size }))} rounded data-testid="rectangle" />
						<Rectangle className={cn(rectangleStyles, TextSkeleton2Styles({ size }))} rounded data-testid="rectangle" />
					</div>
				</SkeletonContainer>
			)}
			{!loading && (
				<div className={GroupHeaderStyles({ size })}>
					<div className={GroupImageStyles({ size })}>
						{imageUrl && !imageLoadError ? (
							<img
								src={imageUrl}
								aria-hidden
								alt=""
								className="h-full w-full object-cover"
								onError={() => setImageLoadError(true)}
							/>
						) : (
							<DefaultFacebookIcon size={size} />
						)}
					</div>
					<p className={GroupNameStyles({ size })}>{groupName}</p>
				</div>
			)}
			<Separator className="m-0 p-0" />
			<SkeletonContainer
				loading={shouldAnimate}
				shapeSize={size}
				className={SkeletonContainerStyles({ size })}
				data-testid="skeleton-container"
			>
				<Circle data-testid="circle" />
				<div className={TextSkeletonContainerStyles({ size })}>
					<Rectangle className={cn(rectangleStyles, TextSkeleton1Styles({ size }))} rounded data-testid="rectangle" />
					<Rectangle className={cn(rectangleStyles, TextSkeleton2Styles({ size }))} rounded data-testid="rectangle" />
				</div>
			</SkeletonContainer>
		</div>
	);
}
